import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { JOB_ROLE_OPTIONS } from '@/lib/constants';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { useSystemLookups } from './hooks/useSystemLookups';

interface WorkflowTask {
  id: string;
  title: string;
  description: string;
  isMandatory: boolean;
  displayOrder: number;
  defaultAssigneeRole: string;
  estimatedHours: number | null;
  taskType: string;
}

interface WorkflowStage {
  id: string;
  name: string;
  displayOrder: number;
  isFinal: boolean;
  linkedStatus: string | null;
  tasks: WorkflowTask[];
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  jobType: string;
  isActive: boolean;
  stageCount?: number;
  taskCount?: number;
  stages: WorkflowStage[];
  createdAt: string;
}

/**
 * WorkflowTemplates — manage workflow templates with stage/task editor.
 * Requirements: 8.2, 15.4
 */
export function WorkflowTemplates() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  // Template being copied (drives the "Copy workflow template" dialog).
  const [copySource, setCopySource] = useState<{ id: string; name: string } | null>(null);
  const [copyName, setCopyName] = useState('');
  const [copyError, setCopyError] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<WorkflowStage | null>(null);
  const [editingTask, setEditingTask] = useState<{ stageId: string; task: WorkflowTask } | null>(
    null,
  );
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const { data: jobTypeLookups } = useSystemLookups('job_types');
  const jobTypeOptions = (jobTypeLookups ?? [])
    .filter((l) => l.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((l) => ({ value: l.code, label: l.label }));

  const { data: templates, isLoading } = useQuery({
    queryKey: ['settings', 'workflow-templates'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown>[] }>(
        '/settings/workflow-templates',
      );
      const raw = response.data.data || [];
      return raw.map((t) => ({
        id: t.id as string,
        name: (t.name || '') as string,
        description: (t.description || '') as string,
        jobType: (t.job_type || t.jobType || '') as string,
        isActive: (t.is_active ?? t.isActive ?? true) as boolean,
        stageCount: (t.stage_count ?? 0) as number,
        taskCount: (t.task_count ?? 0) as number,
        stages: (t.stages || []) as WorkflowStage[],
        createdAt: (t.created_at || t.createdAt || '') as string,
      })) as WorkflowTemplate[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: Partial<WorkflowTemplate>) => {
      const response = await apiClient.post<ApiResponse<WorkflowTemplate>>(
        '/settings/workflow-templates',
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'workflow-templates'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<WorkflowTemplate> }) => {
      const response = await apiClient.put<ApiResponse<WorkflowTemplate>>(
        `/settings/workflow-templates/${id}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'workflow-templates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/settings/workflow-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'workflow-templates'] });
      setSelectedTemplate(null);
    },
  });

  // Copies a template — with all of its stages and tasks — under a new name.
  const duplicateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const response = await apiClient.post<ApiResponse<WorkflowTemplate>>(
        `/settings/workflow-templates/${id}/duplicate`,
        { name },
      );
      return response.data.data;
    },
    onSuccess: (_created, variables) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'workflow-templates'] });
      // Other screens (job form workflow picker, form-builder lookup) cache their own lists.
      queryClient.invalidateQueries({ queryKey: ['jobs', 'templates'] });
      queryClient.invalidateQueries({ queryKey: ['lookup-special', 'workflow_templates'] });
      setCopySource(null);
      toast({
        variant: 'success',
        title: 'Workflow template copied',
        description: `"${variables.name}" was created with all stages and tasks.`,
      });
    },
    onError: (err) => {
      // Keep the dialog open and show why (e.g. the name is already in use).
      const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message;
      setCopyError(message || 'Could not copy the workflow template. Please try again.');
    },
  });

  const openCopyDialog = useCallback((template: { id: string; name: string }) => {
    setCopySource({ id: template.id, name: template.name });
    setCopyName(`${template.name} (Copy)`);
    setCopyError('');
  }, []);

  const submitCopy = useCallback(() => {
    if (!copySource) return;
    const name = copyName.trim();
    if (!name) {
      setCopyError('Enter a name for the new template.');
      return;
    }
    setCopyError('');
    duplicateMutation.mutate({ id: copySource.id, name });
  }, [copySource, copyName, duplicateMutation]);

  const handleCreateTemplate = useCallback(() => {
    const newTemplate: Partial<WorkflowTemplate> = {
      name: 'New Workflow',
      description: '',
      jobType: 'T1_Return',
      isActive: true,
      stages: [],
    };
    createMutation.mutate(newTemplate);
  }, [createMutation]);

  const handleAddStage = useCallback(() => {
    if (!selectedTemplate) return;
    const newStage: WorkflowStage = {
      id: `temp-${Date.now()}`,
      name: '',
      displayOrder: (selectedTemplate.stages?.length ?? 0) + 1,
      isFinal: false,
      linkedStatus: null,
      tasks: [],
    };
    setEditingStage(newStage);
  }, [selectedTemplate]);

  const handleSaveStage = useCallback(
    (stage: WorkflowStage) => {
      if (!selectedTemplate) return;
      const existingIndex = selectedTemplate.stages.findIndex((s) => s.id === stage.id);
      const updatedStages =
        existingIndex >= 0
          ? selectedTemplate.stages.map((s) => (s.id === stage.id ? stage : s))
          : [...selectedTemplate.stages, stage];

      const updated = { ...selectedTemplate, stages: updatedStages };
      setSelectedTemplate(updated);
      updateMutation.mutate({ id: updated.id, data: { stages: updatedStages } });
      setEditingStage(null);
    },
    [selectedTemplate, updateMutation],
  );

  const handleAddTask = useCallback(
    (stageId: string) => {
      if (!selectedTemplate) return;
      const stage = selectedTemplate.stages.find((s) => s.id === stageId);
      if (!stage) return;
      const newTask: WorkflowTask = {
        id: `temp-${Date.now()}`,
        title: '',
        description: '',
        isMandatory: false,
        displayOrder: (stage.tasks?.length ?? 0) + 1,
        defaultAssigneeRole: '',
        estimatedHours: null,
        taskType: 'standard',
      };
      setEditingTask({ stageId, task: newTask });
    },
    [selectedTemplate],
  );

  const handleSaveTask = useCallback(
    (stageId: string, task: WorkflowTask) => {
      if (!selectedTemplate) return;
      const updatedStages = selectedTemplate.stages.map((stage) => {
        if (stage.id !== stageId) return stage;
        const existingIndex = stage.tasks.findIndex((t) => t.id === task.id);
        const updatedTasks =
          existingIndex >= 0
            ? stage.tasks.map((t) => (t.id === task.id ? task : t))
            : [...stage.tasks, task];
        return { ...stage, tasks: updatedTasks };
      });

      const updated = { ...selectedTemplate, stages: updatedStages };
      setSelectedTemplate(updated);
      updateMutation.mutate({ id: updated.id, data: { stages: updatedStages } });
      setEditingTask(null);
    },
    [selectedTemplate, updateMutation],
  );

  const handleDeleteStage = useCallback(
    (stageId: string) => {
      if (!selectedTemplate) return;
      if (!confirm('Are you sure you want to remove this stage and all its tasks?')) return;
      const updatedStages = selectedTemplate.stages
        .filter((s) => s.id !== stageId)
        .map((s, i) => ({ ...s, displayOrder: i + 1 }));

      const updated = { ...selectedTemplate, stages: updatedStages };
      setSelectedTemplate(updated);
      updateMutation.mutate({ id: updated.id, data: { stages: updatedStages } });
    },
    [selectedTemplate, updateMutation],
  );

  const handleDeleteTask = useCallback(
    (stageId: string, taskId: string) => {
      if (!selectedTemplate) return;
      const updatedStages = selectedTemplate.stages.map((stage) => {
        if (stage.id !== stageId) return stage;
        const updatedTasks = stage.tasks
          .filter((t) => t.id !== taskId)
          .map((t, i) => ({ ...t, displayOrder: i + 1 }));
        return { ...stage, tasks: updatedTasks };
      });

      const updated = { ...selectedTemplate, stages: updatedStages };
      setSelectedTemplate(updated);
      updateMutation.mutate({ id: updated.id, data: { stages: updatedStages } });
    },
    [selectedTemplate, updateMutation],
  );

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Template List */}
      <Card>
        <CardHeader
          title="Workflow Templates"
          description="Define reusable workflow templates with stages and tasks for different job types."
          action={
            <Button size="sm" onClick={handleCreateTemplate} loading={createMutation.isPending}>
              New Template
            </Button>
          }
        />
        <CardContent>
          {!templates || templates.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No workflow templates yet. Create one to get started.
            </p>
          ) : (
            <div className="divide-y divide-border dark:divide-border-dark">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
                      onClick={async () => {
                        try {
                          const response = await apiClient.get<{
                            success: boolean;
                            data: Record<string, unknown>;
                          }>(`/settings/workflow-templates/${template.id}`);
                          const raw = response.data.data;
                          // Transform snake_case API response to camelCase
                          const fullTemplate: WorkflowTemplate = {
                            id: raw.id as string,
                            name: raw.name as string,
                            description: (raw.description as string) || '',
                            jobType: (raw.job_type || raw.jobType) as string,
                            isActive: (raw.is_active ?? raw.isActive ?? true) as boolean,
                            createdAt: (raw.created_at || raw.createdAt || '') as string,
                            stages: ((raw.stages as Array<Record<string, unknown>>) || []).map(
                              (s) => ({
                                id: s.id as string,
                                name: s.name as string,
                                displayOrder: (s.display_order ?? s.displayOrder ?? 0) as number,
                                isFinal: (s.is_final ?? s.isFinal ?? false) as boolean,
                                linkedStatus: (s.linked_status ?? s.linkedStatus ?? null) as
                                  | string
                                  | null,
                                tasks: ((s.tasks as Array<Record<string, unknown>>) || []).map(
                                  (t) => ({
                                    id: t.id as string,
                                    title: (t.title || t.name || '') as string,
                                    description: (t.description || '') as string,
                                    isMandatory: (t.is_mandatory ??
                                      t.isMandatory ??
                                      false) as boolean,
                                    displayOrder: (t.display_order ??
                                      t.displayOrder ??
                                      0) as number,
                                    defaultAssigneeRole: (t.default_assignee_role ||
                                      t.defaultAssigneeRole ||
                                      '') as string,
                                    estimatedHours: (t.estimated_hours ??
                                      t.estimatedHours ??
                                      null) as number | null,
                                    taskType: (t.task_type || t.taskType || 'standard') as string,
                                  }),
                                ),
                              }),
                            ),
                          };
                          setSelectedTemplate(fullTemplate);
                        } catch {
                          setSelectedTemplate({ ...template, stages: template.stages || [] });
                        }
                        setIsEditorOpen(true);
                      }}
                    >
                      {template.name}
                    </button>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        {jobTypeLookups?.find(
                          (l) =>
                            l.code ===
                            (template.jobType ||
                              (template as unknown as Record<string, unknown>).job_type),
                        )?.label ||
                          template.jobType ||
                          ((template as unknown as Record<string, unknown>).job_type as string) ||
                          'No job type'}
                      </span>{' '}
                      · {template.stageCount ?? template.stages?.length ?? 0} stages ·{' '}
                      {template.taskCount ?? 0} tasks
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        updateMutation.mutate({
                          id: template.id,
                          data: {
                            is_active: !template.isActive,
                          } as unknown as Partial<WorkflowTemplate>,
                        })
                      }
                      className="cursor-pointer"
                      title={template.isActive ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <Badge variant={template.isActive ? 'success' : 'default'} size="sm">
                        {template.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </button>
                    <Button variant="ghost" size="sm" onClick={() => openCopyDialog(template)}>
                      Copy
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMutation.mutate(template.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Copy Workflow Template Modal */}
      <Modal
        open={!!copySource}
        onClose={() => {
          if (!duplicateMutation.isPending) setCopySource(null);
        }}
        title="Copy workflow template"
        description={
          copySource
            ? `Creates a new template from "${copySource.name}" with all of its stages, tasks and settings. Jobs already using the original are not affected.`
            : undefined
        }
        size="md"
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submitCopy();
          }}
        >
          <Input
            label="New template name"
            value={copyName}
            onChange={(e) => {
              setCopyName(e.target.value);
              if (copyError) setCopyError('');
            }}
            error={copyError || undefined}
            maxLength={200}
            autoFocus
            required
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCopySource(null)}
              disabled={duplicateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" loading={duplicateMutation.isPending}>
              Create copy
            </Button>
          </div>
        </form>
      </Modal>

      {/* Stage/Task Editor Modal */}
      {selectedTemplate && isEditorOpen && (
        <Modal
          open={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          title={`Edit: ${selectedTemplate.name}`}
          size="xl"
        >
          <div className="space-y-4">
            {/* Template Details */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Template Name
                </label>
                <input
                  key={selectedTemplate.id}
                  defaultValue={selectedTemplate.name}
                  onBlur={(e) => {
                    const newName = e.target.value.trim();
                    if (newName && newName !== selectedTemplate.name) {
                      const updated = { ...selectedTemplate, name: newName };
                      setSelectedTemplate(updated);
                      updateMutation.mutate({ id: updated.id, data: { name: newName } });
                    }
                  }}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <Select
                label="Job Type"
                value={selectedTemplate.jobType}
                onChange={(e) => {
                  const updated = { ...selectedTemplate, jobType: e.target.value };
                  setSelectedTemplate(updated);
                  updateMutation.mutate({
                    id: updated.id,
                    data: { job_type: e.target.value } as unknown as Partial<WorkflowTemplate>,
                  });
                }}
                options={jobTypeOptions}
              />
            </div>

            {/* Active/Inactive Toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={selectedTemplate.isActive}
                  onChange={(e) => {
                    const updated = { ...selectedTemplate, isActive: e.target.checked };
                    setSelectedTemplate(updated);
                    updateMutation.mutate({
                      id: updated.id,
                      data: { is_active: e.target.checked } as unknown as Partial<WorkflowTemplate>,
                    });
                  }}
                  className="peer sr-only"
                />
                <div className="peer h-5 w-9 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full dark:bg-gray-600 dark:peer-checked:bg-green-600" />
              </label>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {selectedTemplate.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* Stages */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Stages</h4>
                <Button variant="outline" size="sm" onClick={handleAddStage}>
                  Add Stage
                </Button>
              </div>
              {selectedTemplate.stages.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No stages defined. Add stages to build the workflow.
                </p>
              ) : (
                <div className="space-y-2">
                  {selectedTemplate.stages
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((stage) => {
                      const isExpanded = expandedStages.has(stage.id);
                      const toggleExpand = () => {
                        setExpandedStages((prev) => {
                          const next = new Set(prev);
                          if (next.has(stage.id)) next.delete(stage.id);
                          else next.add(stage.id);
                          return next;
                        });
                      };

                      return (
                        <div
                          key={stage.id}
                          className="rounded-lg border border-border dark:border-border-dark"
                        >
                          <div
                            className="flex cursor-pointer items-center justify-between p-3"
                            onClick={toggleExpand}
                            role="button"
                            aria-expanded={isExpanded}
                            aria-controls={`stage-content-${stage.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <svg
                                className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-400">
                                {stage.displayOrder}
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {stage.name || 'Untitled Stage'}
                              </span>
                              {stage.isFinal && (
                                <Badge variant="info" size="sm">
                                  Final
                                </Badge>
                              )}
                              {stage.linkedStatus && (
                                <Badge variant="default" size="sm">
                                  → {stage.linkedStatus.replace(/_/g, ' ')}
                                </Badge>
                              )}
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {stage.tasks?.length || 0} tasks
                              </span>
                            </div>
                            <div
                              className="ml-auto flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAddTask(stage.id)}
                              >
                                + Task
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingStage(stage)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 dark:text-red-400"
                                onClick={() => handleDeleteStage(stage.id)}
                                aria-label={`Delete stage ${stage.name}`}
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={1.5}
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                  />
                                </svg>
                              </Button>
                            </div>
                          </div>
                          {/* Collapsible tasks section */}
                          {isExpanded && (
                            <div
                              id={`stage-content-${stage.id}`}
                              className="border-t border-border px-3 pb-3 pt-2 dark:border-border-dark"
                            >
                              {stage.tasks && stage.tasks.length > 0 ? (
                                <div className="ml-6 space-y-1">
                                  {stage.tasks
                                    .sort((a, b) => a.displayOrder - b.displayOrder)
                                    .map((task) => (
                                      <div
                                        key={task.id}
                                        className="flex items-center justify-between rounded px-2 py-1 text-sm hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary"
                                      >
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="text-gray-600 dark:text-gray-400">
                                            {task.title || 'Untitled Task'}
                                          </span>
                                          {task.isMandatory ? (
                                            <Badge variant="warning" size="sm">
                                              Required
                                            </Badge>
                                          ) : (
                                            <Badge variant="default" size="sm">
                                              Optional
                                            </Badge>
                                          )}
                                          <Badge
                                            variant={
                                              task.taskType === 'document_upload'
                                                ? 'info'
                                                : 'default'
                                            }
                                            size="sm"
                                          >
                                            {task.taskType === 'document_upload'
                                              ? 'Document Upload'
                                              : 'Standard'}
                                          </Badge>
                                          {task.defaultAssigneeRole && (
                                            <span className="inline-flex items-center gap-1 rounded bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                              <svg
                                                className="h-3 w-3"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={1.5}
                                                aria-hidden="true"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                                                />
                                              </svg>
                                              {JOB_ROLE_OPTIONS.find(
                                                (r) => r.value === task.defaultAssigneeRole,
                                              )?.label || task.defaultAssigneeRole}
                                            </span>
                                          )}
                                        </div>
                                        <div className="ml-auto flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              setEditingTask({ stageId: stage.id, task })
                                            }
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 dark:text-red-400"
                                            onClick={() => handleDeleteTask(stage.id, task.id)}
                                            aria-label={`Delete task ${task.title}`}
                                          >
                                            <svg
                                              className="h-3.5 w-3.5"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                              strokeWidth={1.5}
                                              aria-hidden="true"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                              />
                                            </svg>
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              ) : (
                                <p className="ml-6 text-xs text-gray-400 dark:text-gray-500">
                                  No tasks. Click "+ Task" to add one.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Stage Editor Modal */}
      {editingStage && (
        <StageEditorModal
          stage={editingStage}
          onSave={handleSaveStage}
          onClose={() => setEditingStage(null)}
        />
      )}

      {/* Task Editor Modal */}
      {editingTask && (
        <TaskEditorModal
          stageId={editingTask.stageId}
          task={editingTask.task}
          onSave={handleSaveTask}
          onClose={() => setEditingTask(null)}
        />
      )}
    </div>
  );
}

// ─── Stage Editor ─────────────────────────────────────────────────────────────

interface StageEditorModalProps {
  stage: WorkflowStage;
  onSave: (stage: WorkflowStage) => void;
  onClose: () => void;
}

function StageEditorModal({ stage, onSave, onClose }: StageEditorModalProps) {
  const [form, setForm] = useState<WorkflowStage>(stage);

  return (
    <Modal open onClose={onClose} title="Edit Stage" size="sm">
      <div className="space-y-4">
        <Input
          label="Stage Name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
          placeholder="e.g., Data Collection"
        />
        <Input
          label="Display Order"
          type="number"
          value={String(form.displayOrder)}
          onChange={(e) => setForm((prev) => ({ ...prev, displayOrder: Number(e.target.value) }))}
          min={1}
        />
        <Select
          label="Linked Job Status"
          value={form.linkedStatus || ''}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              linkedStatus: e.target.value || null,
            }))
          }
          options={[
            { value: '', label: 'None (no status change)' },
            { value: 'not_started', label: 'Not Started' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'on_hold', label: 'On Hold' },
            { value: 'waiting_on_client', label: 'Waiting on Client' },
            { value: 'in_review', label: 'In Review' },
            { value: 'complete', label: 'Complete' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={form.isFinal}
            onChange={(e) => setForm((prev) => ({ ...prev, isFinal: e.target.checked }))}
            className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
          />
          Final stage (marks job as complete)
        </label>
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => onSave(form)} disabled={!form.name}>
          Save Stage
        </Button>
      </div>
    </Modal>
  );
}

// ─── Task Editor ──────────────────────────────────────────────────────────────

interface TaskEditorModalProps {
  stageId: string;
  task: WorkflowTask;
  onSave: (stageId: string, task: WorkflowTask) => void;
  onClose: () => void;
}

function TaskEditorModal({ stageId, task, onSave, onClose }: TaskEditorModalProps) {
  const [form, setForm] = useState<WorkflowTask>(task);

  return (
    <Modal open onClose={onClose} title="Edit Task" size="md">
      <div className="space-y-4">
        <Input
          label="Task Title"
          value={form.title}
          onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          required
          placeholder="e.g., Collect T4 slips"
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Optional description of the task"
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Default Assignee Role"
            value={form.defaultAssigneeRole}
            onChange={(e) => setForm((prev) => ({ ...prev, defaultAssigneeRole: e.target.value }))}
            options={[{ value: '', label: 'None' }, ...JOB_ROLE_OPTIONS]}
            placeholder="Select role"
          />
          <Input
            label="Estimated Hours"
            type="number"
            value={form.estimatedHours != null ? String(form.estimatedHours) : ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                estimatedHours: e.target.value ? Number(e.target.value) : null,
              }))
            }
            min={0}
            step="0.5"
            placeholder="0"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={form.isMandatory}
            onChange={(e) => setForm((prev) => ({ ...prev, isMandatory: e.target.checked }))}
            className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
          />
          Mandatory (must be completed before stage transition)
        </label>
        <Select
          label="Task Type"
          value={form.taskType}
          onChange={(e) => setForm((prev) => ({ ...prev, taskType: e.target.value }))}
          options={[
            { value: 'standard', label: 'Standard' },
            { value: 'document_upload', label: 'Document Upload' },
          ]}
          placeholder="Select task type"
        />
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => onSave(stageId, form)} disabled={!form.title}>
          Save Task
        </Button>
      </div>
    </Modal>
  );
}
